// DocumentTable.tsx - Pure prop-based table component (NO STORE DEPENDENCY)

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAnimatedNavigate } from '../../hooks/useAnimatedNavigate';
import { 
  DocumentTextIcon, 
  EyeIcon, 
  ArrowDownTrayIcon,
  TrashIcon,
  SparklesIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ChevronUpDownIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  XMarkIcon,
  TagIcon,
  UserIcon,
  BriefcaseIcon,
  PencilSquareIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';

import { DocumentListItem } from './DocumentAPI';
import { DocumentCategory } from './DocumentCategoryAPI';
import { useKeycloak } from '@react-keycloak/web';
import { PracticeAreaDocumentTypeModal } from './components/PracticeAreaDocumentTypeModal';
import { useUpdateDocumentCategory } from './hooks/useDocumentMutations';
import { listAllDocumentTypeOptions, resolveDocumentTypeKey } from './taxonomy/legalDocumentTaxonomy';

const documentTitleClass =
  'block min-w-0 max-w-full text-left text-sm font-semibold leading-5 text-[var(--brand-text-primary)] hover:text-[var(--brand-accent-primary)] whitespace-normal break-words [overflow-wrap:anywhere] transition-colors';
const documentCardTitleClass =
  'min-w-0 text-sm font-semibold text-[var(--brand-text-primary)] hover:text-[var(--brand-accent-primary)] line-clamp-2 text-left flex-1 break-words [overflow-wrap:anywhere] transition-colors';
const clientBadgeClass =
  'inline-flex items-center rounded bg-[var(--brand-accent-primary-soft)] text-[var(--brand-accent-deep)] hover:bg-[var(--brand-state-selected-hover)] transition-colors cursor-pointer';
const matterBadgeClass =
  'inline-flex items-center rounded bg-[var(--brand-status-success-bg)] text-[var(--brand-status-success)] hover:bg-[var(--brand-status-success-bg)] transition-colors cursor-pointer';

// Loading Skeleton Component to prevent layout shifts
const TableSkeleton: React.FC = () => (
  <div className="animate-pulse">
    <div className="border-b border-gray-200 dark:border-gray-700 p-4">
      <div className="flex items-center justify-between">
        <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-32"></div>
        <div className="flex items-center space-x-2">
          <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded w-64"></div>
          <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded w-10"></div>
        </div>
      </div>
    </div>
    
    <div className="overflow-x-auto min-h-[400px]">
      <table className="w-full">
        <thead className="bg-gray-50 dark:bg-gray-700">
          <tr>
            <th className="w-12 px-4 py-3 text-left">
              <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-4"></div>
            </th>
            <th className="px-4 py-3 text-left">
              <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-20"></div>
            </th>
            <th className="px-4 py-3 text-left">
              <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-16"></div>
            </th>
            <th className="px-4 py-3 text-left">
              <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-20"></div>
            </th>
            <th className="px-4 py-3 text-right">
              <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-16"></div>
            </th>
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
          {[...Array(5)].map((_, i) => (
            <tr key={i}>
              <td className="px-4 py-4">
                <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-4"></div>
              </td>
              <td className="px-4 py-4">
                <div className="flex items-center space-x-3">
                  <div className="h-5 w-5 bg-gray-200 dark:bg-gray-600 rounded"></div>
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-32 mb-2"></div>
                    <div className="h-3 bg-gray-200 dark:bg-gray-600 rounded w-24"></div>
                  </div>
                </div>
              </td>
              <td className="px-4 py-4">
                <div className="h-6 bg-gray-200 dark:bg-gray-600 rounded w-20"></div>
              </td>
              <td className="px-4 py-4">
                <div className="space-y-1">
                  <div className="h-3 bg-gray-200 dark:bg-gray-600 rounded w-16"></div>
                  <div className="h-3 bg-gray-200 dark:bg-gray-600 rounded w-20"></div>
                </div>
              </td>
              <td className="px-4 py-4 text-right">
                <div className="flex items-center justify-end space-x-2">
                  {[...Array(4)].map((_, j) => (
                    <div key={j} className="h-4 w-4 bg-gray-200 dark:bg-gray-600 rounded"></div>
                  ))}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

// Types
export type ViewMode = 'table' | 'grid';

// Props interface - everything passed down from parent
interface DocumentTableProps {
  documents: DocumentListItem[];
  isLoading?: boolean;
  error?: string | null;
  viewMode?: ViewMode;
  
  // Pagination props
  currentPage: number;
  pageSize: number;
  totalDocuments: number;
  totalPages: number;
  
  // Filter and search props
  filters: {
    sortBy?: 'title' | 'created_at' | 'updated_at' | 'size' | 'type';
    sortOrder?: 'asc' | 'desc';
    status?: string;
    mimeType?: string;
    createdAfter?: string;
    createdBefore?: string;
  };
  searchQuery: string;
  
  // Selection props
  selectedDocumentIds: Set<string>;
  
  // Event handlers
  onDocumentClick?: (document: DocumentListItem) => void;
  onViewDocument?: (document: DocumentListItem) => void;
  onEditDocumentInfo?: (document: DocumentListItem) => void;
  onDownloadDocument?: (document: DocumentListItem) => void;
  onDeleteDocument?: (document: DocumentListItem) => void;
  onDocumentProcess?: (document: DocumentListItem) => void;
  onNewVersion?: (document: DocumentListItem) => void;
  onDocumentUpdate?: (updateData: {
    documentId: string;
    categoryId?: string | null;
    categoryName?: string | null;
    title?: string;
  }) => void;
  
  // Handler props for state changes
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onFiltersChange: (filters: any) => void;
  onSearchChange: (query: string) => void;
  onToggleSelection: (documentId: string) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  
  className?: string;
}

type SortableColumn = 'title' | 'created_at' | 'updated_at' | 'size' | 'type';

const DocumentTable: React.FC<DocumentTableProps> = ({
  documents,
  isLoading,
  error,
  viewMode = 'table',
  currentPage,
  pageSize,
  totalDocuments,
  totalPages,
  filters,
  searchQuery,
  selectedDocumentIds,
  onDocumentClick,
  onViewDocument,
  onEditDocumentInfo,
  onDownloadDocument,
  onDeleteDocument,
  onDocumentProcess,
  onDocumentUpdate: _onDocumentUpdate, // Accepted but ignored to maintain interface compatibility
  onPageChange,
  onPageSizeChange,
  onFiltersChange,
  onSearchChange,
  onToggleSelection,
  onSelectAll,
  onDeselectAll,
  className = '',
}) => {
  const navigate = useAnimatedNavigate();
  
  // Local state for search input (debounced)
  const [searchInput, setSearchInput] = useState(searchQuery);
  const [showFilters, setShowFilters] = useState(false);
  
  // NEW: Category dropdown state management (simplified)
  const [openCategoryDropdown, setOpenCategoryDropdown] = useState<string | null>(null);
  const [categories, setCategories] = useState<DocumentCategory[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const dropdownRefs = useRef<Record<string, HTMLDivElement | null>>({});
  
  // NEW: Local state for per-document updating status
  const [updatingDocuments, setUpdatingDocuments] = useState<Set<string>>(new Set());
  
  // NEW: Rename state management
  const [renamingDocumentId, setRenamingDocumentId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState<string>('');

  // ✨ SMOOTH REACT QUERY MUTATION - prevents full reload to reduce shaking
  const updateCategoryMutation = useUpdateDocumentCategory((updateData) => {
    _onDocumentUpdate?.(updateData);
  });
  const { keycloak } = useKeycloak();
  
  // Ref for indeterminate checkbox
  const selectAllCheckboxRef = useRef<HTMLInputElement>(null);
  const hasMountedSearchEffect = useRef(false);

  // Helper functions - Native file type icons
  const getFileIcon = (mimeType?: string) => {
    if (!mimeType) return <DocumentTextIcon className="w-5 h-5 text-gray-400" />;
    
    // PDF files - red PDF icon
    if (mimeType.includes('pdf')) {
      return (
        <div className="w-5 h-5 bg-red-500 rounded-sm flex items-center justify-center text-white text-[8px] font-bold">
          PDF
        </div>
      );
    }
    
    // Microsoft Word files - blue Word icon
    if (mimeType.includes('word') || mimeType.includes('msword') || mimeType.includes('wordprocessingml')) {
      return (
        <div className="w-5 h-5 bg-lex-accent rounded-sm flex items-center justify-center text-white text-[8px] font-bold">
          W
        </div>
      );
    }
    
    // Microsoft Excel files - green Excel icon
    if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) {
      return (
        <div className="w-5 h-5 bg-green-600 rounded-sm flex items-center justify-center text-white text-[8px] font-bold">
          X
        </div>
      );
    }
    
    // Microsoft PowerPoint files - orange PowerPoint icon
    if (mimeType.includes('powerpoint') || mimeType.includes('presentation')) {
      return (
        <div className="w-5 h-5 bg-orange-600 rounded-sm flex items-center justify-center text-white text-[8px] font-bold">
          P
        </div>
      );
    }
    
    // Text files (JSON, TXT, etc.) - document text icon like in the image
    if (mimeType.includes('text') || mimeType.includes('json') || mimeType.includes('plain')) {
      return <DocumentTextIcon className="w-5 h-5 text-[var(--brand-text-secondary)]" />;
    }
    
    // Image files - purple image icon
    if (mimeType.includes('image')) {
      return (
        <div className="w-5 h-5 bg-purple-600 rounded-sm flex items-center justify-center text-white text-[8px] font-bold">
          IMG
        </div>
      );
    }
    
    // Default - gray document icon
    return <DocumentTextIcon className="w-5 h-5 text-[var(--brand-text-tertiary)]" />;
  };



  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatVersionLabel = (versionNumber?: number | string | null) => {
    if (versionNumber === undefined || versionNumber === null || versionNumber === '') {
      return '';
    }

    const raw = String(versionNumber);
    return raw.toLowerCase().startsWith('v') ? raw : `v${raw}`;
  };

  const getDocumentModifiedInfo = (document: DocumentListItem) => {
    const latestVersion = document.latest_version;
    const source = latestVersion?.checkin_metadata?.source;
    const versionLabel = formatVersionLabel(latestVersion?.version_number);
    const candidates = [
      { value: latestVersion?.checked_in_at || undefined, label: 'Checked in' },
      {
        value: latestVersion?.updated_at,
        label: source === 'word-addin' ? 'Saved in Word' : 'Version updated',
      },
      { value: latestVersion?.uploaded_at, label: 'New version' },
      { value: document.effective_modified_at, label: 'Latest DMS activity' },
      { value: document.updated_at, label: 'Document updated' },
    ]
      .map((candidate) => ({
        ...candidate,
        time: candidate.value ? new Date(candidate.value).getTime() : Number.NaN,
      }))
      .filter((candidate) => Number.isFinite(candidate.time))
      .sort((a, b) => b.time - a.time);

    const selected = candidates[0] || {
      value: document.updated_at,
      label: 'Document updated',
      time: new Date(document.updated_at).getTime(),
    };

    return {
      timestamp: selected.value || document.updated_at,
      label: [selected.label, versionLabel].filter(Boolean).join(' • '),
    };
  };

  const isWordDocument = (document: DocumentListItem): boolean => {
    const editorType = (document as any)?.editor_type;
    const mimeType = (document.latest_version?.mime_type || '').toLowerCase();
    const originalName = (document.latest_version?.original_name || document.title || '').toLowerCase();
    return (
      editorType === 'msword' ||
      mimeType.includes('word') ||
      mimeType.includes('msword') ||
      mimeType.includes('wordprocessingml') ||
      originalName.endsWith('.docx') ||
      originalName.endsWith('.doc')
    );
  };

  // Helper function to open file in native editor (download)
  const handleOpenInNativeEditor = async (document: DocumentListItem) => {
    try {
      const docId = document.document_id || document.id;
      const versionId = document.latest_version?.version_id;
      if (docId && versionId && isWordDocument(document)) {
        navigate(`/documents/${docId}/word-editor?version=${versionId}&launch=word`);
        return;
      }
      if (docId && versionId) {
        const blob = await fetch(`${window.location.origin}/api/v1/dms/documents/${docId}/versions/${versionId}/download`, {
          headers: {
            'Authorization': `Bearer ${keycloak.token}`,
          },
        }).then(response => {
          if (!response.ok) {
            throw new Error(`Failed to download document: ${response.statusText}`);
          }
          return response.blob();
        });
        
        const url = URL.createObjectURL(blob);
        const a = window.document.createElement('a');
        a.href = url;
        a.download = document.latest_version?.original_name || document.title;
        window.document.body.appendChild(a);
        a.click();
        window.document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Failed to open document in native editor:', error);
      alert('Failed to open document in native editor. Please try again.');
    }
  };

  const getSortIcon = (column: SortableColumn) => {
    if (filters.sortBy !== column) {
      return <ChevronUpDownIcon className="w-4 h-4 text-[var(--brand-text-tertiary)]" />;
    }
    return filters.sortOrder === 'asc' 
      ? <ChevronUpIcon className="w-4 h-4 text-lex-accent" />
      : <ChevronDownIcon className="w-4 h-4 text-lex-accent" />;
  };

  // Navigation handlers for badges
  const handleClientClick = (document: DocumentListItem) => {
    // Use client_id for direct navigation if available
    if (document.client_id) {
      navigate(`/contacts/${document.client_id}`);
    } else if (document.client_name) {
      // Fallback: navigate to contacts page with search filter
      navigate(`/contacts?search=${encodeURIComponent(document.client_name)}`);
    }
  };

  const handleMatterClick = (document: DocumentListItem) => {
    // Use matter_id for direct navigation if available
    if (document.matter_id) {
      navigate(`/matters/${document.matter_id}`);
    } else if (document.matter_description) {
      // Fallback: navigate to matters page with search filter
      navigate(`/matters?search=${encodeURIComponent(document.matter_description)}`);
    }
  };



  const handleSort = (column: SortableColumn) => {
    const newOrder = filters.sortBy === column && filters.sortOrder === 'asc' ? 'desc' : 'asc';
    onFiltersChange({ ...filters, sortBy: column, sortOrder: newOrder });
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    // Search is now handled by debounced effect
  };

  const handleSearchClear = () => {
    setSearchInput('');
    // Clear search is handled by debounced effect
  };

  const handleSelectAll = () => {
    if (selectedDocumentIds.size === documents.length) {
      onDeselectAll();
    } else {
      onSelectAll();
    }
  };

  const isAllSelected = selectedDocumentIds.size === documents.length && documents.length > 0;
  const isPartiallySelected = selectedDocumentIds.size > 0 && selectedDocumentIds.size < documents.length;
  
  // Fix 1: Move indeterminate logic to useEffect to prevent render mutations
  useEffect(() => {
    if (selectAllCheckboxRef.current) {
      selectAllCheckboxRef.current.indeterminate = isPartiallySelected;
    }
  }, [isPartiallySelected]);

  // Fix 3: Debounce search input to prevent rapid re-renders
  const debouncedSearch = useCallback((query: string) => {
    const timeoutId = setTimeout(() => {
      onSearchChange(query);
    }, 300);
    
    return () => clearTimeout(timeoutId);
  }, [onSearchChange]);

  useEffect(() => {
    setSearchInput(searchQuery);
  }, [searchQuery]);

  useEffect(() => {
    if (!hasMountedSearchEffect.current) {
      hasMountedSearchEffect.current = true;
      return;
    }

    if (searchInput === searchQuery) {
      return;
    }

    const cleanup = debouncedSearch(searchInput);
    return cleanup;
  }, [searchInput, searchQuery, debouncedSearch]);

  const getDocumentCategoryName = (document: DocumentListItem) => {
    const canonicalType = resolveDocumentTypeKey(document.primary_type || null);
    if (canonicalType) {
      return listAllDocumentTypeOptions().find(option => option.value === canonicalType)?.label || canonicalType;
    }
    if (document.category_name) return document.category_name;
    return categories.find(category => category.id === document.category_id)?.name || null;
  };

  // Load firm-scoped canonical document categories on mount
  useEffect(() => {
    if (!keycloak.token) return;

    const loadAll = async () => {
      setLoadingCategories(true);

      const cats = await fetch(`${window.location.origin}/api/v1/dms/categories`, {
        headers: {
          'Authorization': `Bearer ${keycloak.token}`,
          'Content-Type': 'application/json',
        },
      }).then(res => res.ok ? res.json() : { categories: [] })
        .then(data => data.categories || data.data || data || [])
        .catch(() => [] as DocumentCategory[]);

      setCategories(cats);
      setLoadingCategories(false);
    };

    loadAll();
  }, [keycloak.token]);

  // NEW: Handle click outside dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openCategoryDropdown) {
        const currentDropdown = dropdownRefs.current[openCategoryDropdown];
        if (currentDropdown && !currentDropdown.contains(event.target as Node)) {
          setOpenCategoryDropdown(null);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openCategoryDropdown]);





  // ✨ SMOOTH CATEGORY UPDATE - optimized for minimal screen shaking
  const updateDocumentCategory = (documentId: string, categoryId: string, closeDropdown = true) => {
    // Find the selected category name
    const selectedCategory = categories.find(cat => cat.id === categoryId);
    const categoryName = selectedCategory?.name || null;
    
    // Set this document as updating
    setUpdatingDocuments(prev => new Set([...prev, documentId]));
    
    if (closeDropdown) {
      setOpenCategoryDropdown(null);
    }
    
    // React Query handles optimistic updates, error handling, and cache invalidation automatically
    updateCategoryMutation.mutate({ 
      documentId, 
      categoryId: categoryId || null,
      categoryName: categoryName
    }, {
      onSuccess: () => {
        // Remove from updating set immediately
        setUpdatingDocuments(prev => {
          const newSet = new Set(prev);
          newSet.delete(documentId);
          return newSet;
        });
      },
      onError: () => {
        // Remove from updating set on error too
        setUpdatingDocuments(prev => {
          const newSet = new Set(prev);
          newSet.delete(documentId);
          return newSet;
        });
      }
    });
  };

  // NEW: Handle category dropdown toggle
  const handleCategoryDropdownToggle = (documentId: string) => {
    const document = documents.find((candidate) => candidate.document_id === documentId);
    if (document && onEditDocumentInfo) {
      onEditDocumentInfo(document);
      return;
    }

    // Manual trigger to load categories if they're not loaded
    if (categories.length === 0 && !loadingCategories) {
      loadCategoriesManually();
    }
    
    setOpenCategoryDropdown(openCategoryDropdown === documentId ? null : documentId);
  };

  // Manual categories loading function for the picker fallback path.
  const loadCategoriesManually = async () => {
    try {
      setLoadingCategories(true);
      
      const response = await fetch(`${window.location.origin}/api/v1/dms/categories`, {
        headers: {
          'Authorization': `Bearer ${keycloak.token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Manual API error:', errorText);
        return;
      }

      const data = await response.json();
      
      const categoryData = data.categories || data.data || data || [];
      setCategories(categoryData);
    } catch (error) {
      console.error('Manual categories loading failed:', error);
    } finally {
      setLoadingCategories(false);
    }
  };

  // NEW: Rename handlers
  const handleStartRename = (document: DocumentListItem) => {
    setRenamingDocumentId(document.document_id);
    setRenameValue(document.title);
  };

  const handleCancelRename = () => {
    setRenamingDocumentId(null);
    setRenameValue('');
  };

  const handleSaveRename = async (documentId: string) => {
    if (!renameValue.trim() || renameValue === documents.find(d => d.document_id === documentId)?.title) {
      handleCancelRename();
      return;
    }

    try {
      setUpdatingDocuments(prev => new Set([...prev, documentId]));
      
      // Call the rename API
      const response = await fetch(`${window.location.origin}/api/v1/dms/documents/${documentId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${keycloak.token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ title: renameValue.trim() }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to rename document: ${response.statusText}`);
      }

      // Call the parent's document update handler for optimistic updates
      if (_onDocumentUpdate) {
        const currentDoc = documents.find(d => d.document_id === documentId);
        _onDocumentUpdate({
          documentId,
          categoryId: currentDoc?.category_id,
          categoryName: currentDoc?.category_name,
          title: renameValue.trim(),
        });
      }

      // The parent component (DMSDashboard) will handle the state update

      handleCancelRename();
    } catch (error) {
      console.error('Failed to rename document:', error);
      alert(`Failed to rename document: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setUpdatingDocuments(prev => {
        const newSet = new Set(prev);
        newSet.delete(documentId);
        return newSet;
      });
    }
  };

  // NEW: Document Type Modal Component
  const CategoryDropdown: React.FC<{ document: DocumentListItem }> = ({ document }) => {
    if (openCategoryDropdown !== document.document_id) return null;

    return (
      <div
        ref={(el) => {
          dropdownRefs.current[document.document_id] = el;
        }}
      >
        <PracticeAreaDocumentTypeModal
          isOpen={openCategoryDropdown === document.document_id}
          onClose={() => setOpenCategoryDropdown(null)}
          documentTypes={categories}
          selectedDocumentTypeId={document.category_id || ''}
          onDocumentTypeSelect={(documentTypeId) => {
            updateDocumentCategory(document.document_id, documentTypeId, false);
          }}
          title="Assign Document Type"
          loading={loadingCategories || updateCategoryMutation.isPending}
        />
      </div>
    );
  };

    // NEW: Memoized Document Row Component to prevent unnecessary re-renders
  const DocumentRow = React.memo<{ document: DocumentListItem }>(({ document }) => {
    const isSelected = selectedDocumentIds.has(document.document_id);
    const isUpdatingThisDoc = updatingDocuments.has(document.document_id);
    
    // Memoize expensive computations to prevent re-renders
    const documentId = document.document_id;
    const mimeType = document.latest_version?.mime_type;
    const clientName = document.client_name;
    const matterDescription = document.matter_description;
    const categoryName = getDocumentCategoryName(document);
    const createdAt = document.created_at;
    const modifiedInfo = getDocumentModifiedInfo(document);
    const title = document.title;

    const status = document.status;
    
    return (
      <tr
        key={documentId}
        className={`group hover:bg-[var(--brand-surface-hover)] transition-colors ${
          isSelected ? 'bg-[var(--brand-state-selected)]' : ''
        }`}
      >
        {/* Selection Checkbox */}
        <td className="px-4 py-4 whitespace-nowrap text-sm text-[var(--brand-text-primary)]">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={(e) => {
              e.stopPropagation();
              onToggleSelection(documentId);
            }}
            className="focus:ring-lex-accent h-4 w-4 text-lex-accent border-gray-300 dark:border-gray-600 rounded"
          />
        </td>

        {/* Document Info with stacked Client/Matter badges */}
        <td className="px-4 py-4 align-top">
          <div className="flex min-w-0 items-start space-x-3">
            <div className="flex-shrink-0">
              <button
                onClick={() => handleOpenInNativeEditor(document)}
                className="hover:scale-110 transition-transform cursor-pointer"
                title={isWordDocument(document) ? 'Open in Word with EdgeLex tracking' : 'Open in native editor (download)'}
              >
                {getFileIcon(mimeType)}
              </button>
            </div>
            <div className="min-w-0 flex-1">
              {renamingDocumentId === documentId ? (
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={renameValue}
                    onChange={(e) => setRenameValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleSaveRename(documentId);
                      } else if (e.key === 'Escape') {
                        handleCancelRename();
                      }
                    }}
                    className="text-sm font-medium bg-white dark:bg-gray-700 border border-lex-accent rounded px-2 py-1 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-lex-accent w-full"
                    autoFocus
                  />
                  <button
                    onClick={() => handleSaveRename(documentId)}
                    className="text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300"
                    title="Save rename"
                  >
                    <CheckIcon className="w-4 h-4" />
                  </button>
                  <button
                    onClick={handleCancelRename}
                    className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                    title="Cancel rename"
                  >
                    <XMarkIcon className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="flex min-w-0 items-start space-x-2">
                  <button
                    onClick={() => onDocumentClick?.(document)}
                    className={documentTitleClass}
                  >
                    {title}
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleStartRename(document);
                    }}
                    className="mt-0.5 flex-shrink-0 text-gray-400 hover:text-lex-accent dark:hover:text-lex-accent opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Rename document"
                  >
                    <PencilSquareIcon className="w-4 h-4" />
                  </button>
                </div>
              )}
              {/* Client and Matter Info as stacked badges */}
              <div className="space-y-1 mt-1">
                {clientName && (
                  <div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleClientClick(document);
                      }}
                      className={`${clientBadgeClass} px-2 py-0.5 text-xs`}
                    >
                      <UserIcon className="w-3 h-3 mr-1" />
                      Client: {clientName}
                    </button>
                  </div>
                )}
                {matterDescription && (
                  <div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMatterClick(document);
                      }}
                      className={`${matterBadgeClass} px-2 py-0.5 text-xs`}
                    >
                      <BriefcaseIcon className="w-3 h-3 mr-1" />
                      Matter: {matterDescription}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </td>

        {/* Category - MEMOIZED with document-specific loading */}
        <td className="px-4 py-4 whitespace-nowrap text-sm">
          <div className="relative">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleCategoryDropdownToggle(documentId);
              }}
              disabled={isUpdatingThisDoc}
              className={`inline-flex items-center px-2 py-0.5 rounded text-xs transition-all duration-200 ease-in-out cursor-pointer disabled:opacity-50 transform hover:scale-105 ${
                categoryName
                  ? openCategoryDropdown === documentId
                    ? 'bg-purple-200 dark:bg-purple-800 text-purple-900 dark:text-purple-200 shadow-md' 
                    : 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-300 hover:bg-purple-200 dark:hover:bg-purple-800 hover:shadow-sm'
                  : openCategoryDropdown === documentId
                    ? 'bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 shadow-md' 
                    : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-700 dark:hover:text-gray-300 hover:shadow-sm'
              }`}
            >
              {isUpdatingThisDoc ? (
                <>
                  <div className="w-3 h-3 border border-gray-400 border-t-transparent rounded-full animate-spin mr-1" />
                  Updating...
                </>
              ) : (
                <>
                  <TagIcon className="w-3 h-3 mr-1" />
                  {categoryName || 'No Type Set'}
                  <ChevronDownIcon className={`w-3 h-3 ml-1 transition-transform ${openCategoryDropdown === documentId ? 'rotate-180' : ''}`} />
                </>
              )}
            </button>
            
            <CategoryDropdown document={document} />
          </div>
        </td>

        {/* Modified Date */}
        <td className="px-4 py-4 whitespace-nowrap text-sm text-[var(--brand-text-secondary)]">
          <div className="text-sm font-medium text-[var(--brand-text-primary)]">
            {formatDate(modifiedInfo.timestamp)}
          </div>
          <div className="text-sm text-[var(--brand-text-secondary)]">
            {modifiedInfo.label}
          </div>
          <div className="text-xs text-[var(--brand-text-tertiary)]">
            Created {formatDate(createdAt)}
          </div>
        </td>

        {/* Actions */}
        <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium">
          <div className="flex items-center justify-end space-x-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onViewDocument?.(document);
              }}
              className="text-lex-accent hover:text-lex-accent-hover"
              title="View Document"
            >
              <EyeIcon className="w-4 h-4" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDownloadDocument?.(document);
              }}
              className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300"
              title="Download Document"
            >
              <ArrowDownTrayIcon className="w-4 h-4" />
            </button>
            
            {status?.toLowerCase() !== 'processing' && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDocumentProcess?.(document);
                }}
                className="text-purple-600 hover:text-purple-900 dark:text-purple-400 dark:hover:text-purple-300"
                title="Process with AI"
              >
                <SparklesIcon className="w-4 h-4" />
              </button>
            )}
            
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDeleteDocument?.(document);
              }}
              className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
              title="Delete Document"
            >
              <TrashIcon className="w-4 h-4" />
            </button>
          </div>
        </td>
      </tr>
    );
  });

  // NEW: Memoized Document Card Component for grid view
  const DocumentCard = React.memo<{ document: DocumentListItem }>(({ document }) => {
    const isSelected = selectedDocumentIds.has(document.document_id);
    const isUpdatingThisDoc = updatingDocuments.has(document.document_id);
    
    // Memoize expensive computations to prevent re-renders
    const documentId = document.document_id;
    const mimeType = document.latest_version?.mime_type;
    const clientName = document.client_name;
    const matterDescription = document.matter_description;
    const categoryName = getDocumentCategoryName(document);
    const createdAt = document.created_at;
    const modifiedInfo = getDocumentModifiedInfo(document);
    const title = document.title;

    const status = document.status;
    
    return (
      <div
        key={documentId}
        className={`bg-[var(--brand-surface-card)] rounded-lg border border-[var(--brand-border)] p-4 hover:shadow-md transition-all duration-200 ${
          isSelected ? 'ring-2 ring-[var(--brand-accent-primary)] bg-[var(--brand-state-selected)]' : 'hover:border-[var(--brand-border-strong)]'
        }`}
      >
        {/* Card Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={isSelected}
              onChange={(e) => {
                e.stopPropagation();
                onToggleSelection(documentId);
              }}
              className="focus:ring-lex-accent h-4 w-4 text-lex-accent border-gray-300 dark:border-gray-600 rounded"
            />
            <button
              onClick={() => handleOpenInNativeEditor(document)}
              className="hover:scale-110 transition-transform cursor-pointer"
              title={isWordDocument(document) ? 'Open in Word with EdgeLex tracking' : 'Open in native editor (download)'}
            >
              {getFileIcon(mimeType)}
            </button>
          </div>
          
          {/* Action buttons */}
          <div className="flex items-center space-x-1">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onViewDocument?.(document);
              }}
              className="p-1 text-lex-accent hover:text-lex-accent-hover hover:bg-lex-state-hover rounded"
              title="View Document"
            >
              <EyeIcon className="w-4 h-4" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDownloadDocument?.(document);
              }}
              className="p-1 text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300 hover:bg-green-50 dark:hover:bg-green-900/20 rounded"
              title="Download Document"
            >
              <ArrowDownTrayIcon className="w-4 h-4" />
            </button>
            
            {status?.toLowerCase() !== 'processing' && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDocumentProcess?.(document);
                }}
                className="p-1 text-purple-600 hover:text-purple-900 dark:text-purple-400 dark:hover:text-purple-300 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded"
                title="Process with AI"
              >
                <SparklesIcon className="w-4 h-4" />
              </button>
            )}
            
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDeleteDocument?.(document);
              }}
              className="p-1 text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
              title="Delete Document"
            >
              <TrashIcon className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Document Title */}
        <div className="mb-3">
          {renamingDocumentId === documentId ? (
            <div className="flex items-center space-x-2">
              <input
                type="text"
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSaveRename(documentId);
                  } else if (e.key === 'Escape') {
                    handleCancelRename();
                  }
                }}
                className="text-sm font-medium bg-white dark:bg-gray-700 border border-lex-accent rounded px-2 py-1 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-lex-accent flex-1"
                autoFocus
              />
              <button
                onClick={() => handleSaveRename(documentId)}
                className="text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300"
                title="Save rename"
              >
                <CheckIcon className="w-4 h-4" />
              </button>
              <button
                onClick={handleCancelRename}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                title="Cancel rename"
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div className="flex items-center space-x-2 group">
              <button
                onClick={() => onDocumentClick?.(document)}
                className={documentCardTitleClass}
              >
                {title}
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleStartRename(document);
                }}
                className="text-gray-400 hover:text-lex-accent dark:hover:text-lex-accent opacity-0 group-hover:opacity-100 transition-opacity"
                title="Rename document"
              >
                <PencilSquareIcon className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>

        {/* Document Type */}
        <div className="mb-3">
          <div className="relative">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleCategoryDropdownToggle(documentId);
              }}
              disabled={isUpdatingThisDoc}
              className={`inline-flex items-center px-2 py-1 rounded text-xs transition-all duration-200 ease-in-out cursor-pointer disabled:opacity-50 w-full justify-center ${
                categoryName
                  ? openCategoryDropdown === documentId
                    ? 'bg-purple-200 dark:bg-purple-800 text-purple-900 dark:text-purple-200 shadow-md' 
                    : 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-300 hover:bg-purple-200 dark:hover:bg-purple-800'
                  : openCategoryDropdown === documentId
                    ? 'bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 shadow-md' 
                    : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              {isUpdatingThisDoc ? (
                <>
                  <div className="w-3 h-3 border border-gray-400 border-t-transparent rounded-full animate-spin mr-1" />
                  Updating...
                </>
              ) : (
                <>
                  <TagIcon className="w-3 h-3 mr-1" />
                  {categoryName || 'No Type Set'}
                  <ChevronDownIcon className={`w-3 h-3 ml-1 transition-transform ${openCategoryDropdown === documentId ? 'rotate-180' : ''}`} />
                </>
              )}
            </button>
            
            <CategoryDropdown document={document} />
          </div>
        </div>

        {/* Client and Matter Info */}
        <div className="space-y-2 mb-3">
          {clientName && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleClientClick(document);
              }}
              className={`${clientBadgeClass} px-2 py-1 text-xs w-full justify-start`}
            >
              <UserIcon className="w-3 h-3 mr-1 flex-shrink-0" />
              <span className="truncate">Client: {clientName}</span>
            </button>
          )}
          {matterDescription && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleMatterClick(document);
              }}
              className={`${matterBadgeClass} px-2 py-1 text-xs w-full justify-start`}
            >
              <BriefcaseIcon className="w-3 h-3 mr-1 flex-shrink-0" />
              <span className="truncate">Matter: {matterDescription}</span>
            </button>
          )}
        </div>

        {/* Dates */}
        <div className="text-xs text-[var(--brand-text-secondary)] border-t border-[var(--brand-border)] pt-2">
          <div className="flex justify-between">
            <span>Modified</span>
            <span>{formatDate(modifiedInfo.timestamp)}</span>
          </div>
          <div className="flex justify-between mt-1">
            <span>Activity</span>
            <span className="text-right">{modifiedInfo.label}</span>
          </div>
          <div className="flex justify-between mt-1">
            <span>Created</span>
            <span>{formatDate(createdAt)}</span>
          </div>
        </div>
      </div>
    );
  });

  // Pagination component - SAME UI, PROPS-BASED
  const PaginationControls = () => {
    const pages = [];
    const startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(totalPages, currentPage + 2);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => onPageChange(i)}
          className={`px-3 py-1 text-sm rounded ${
            i === currentPage
              ? 'bg-lex-accent text-lex-accent-contrast'
              : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
          }`}
        >
          {i}
        </button>
      );
    }

    return (
      <div className="flex items-center justify-between px-6 py-3 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
        <div className="flex items-center space-x-2 text-sm text-gray-700 dark:text-gray-300">
          <span>Show</span>
          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          <span>per page</span>
          <span className="ml-4">
            Showing {Math.min((currentPage - 1) * pageSize + 1, totalDocuments)} to{' '}
            {Math.min(currentPage * pageSize, totalDocuments)} of {totalDocuments} results
          </span>
        </div>
        
        <div className="flex items-center space-x-2">
          <button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="px-3 py-1 text-sm bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          
          {pages}
          
          <button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="px-3 py-1 text-sm bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      </div>
    );
  };

  // Filter panel
  const FilterPanel = () => {
    if (!showFilters) return null;

    return (
      <div className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Status
            </label>
                          <select
                value={filters.status || ''}
                onChange={(e) => onFiltersChange({ ...filters, status: e.target.value as 'Active' | 'Archived' | 'deleted' })}
                className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              >
              <option value="">All</option>
              <option value="Active">Active</option>
              <option value="Archived">Archived</option>
              <option value="deleted">Deleted</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              File Type
            </label>
            <select
              value={filters.mimeType || ''}
              onChange={(e) => onFiltersChange({ ...filters, mimeType: e.target.value || undefined })}
              className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value="">All Types</option>
              <option value="application/pdf">PDF</option>
              <option value="application/msword">Word</option>
              <option value="application/vnd.ms-excel">Excel</option>
              <option value="application/vnd.ms-powerpoint">PowerPoint</option>
              <option value="image/">Images</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Date Range
            </label>
            <div className="flex space-x-2">
              <input
                type="date"
                value={filters.createdAfter || ''}
                onChange={(e) => onFiltersChange({ ...filters, createdAfter: e.target.value || undefined })}
                className="flex-1 border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
              <input
                type="date"
                value={filters.createdBefore || ''}
                onChange={(e) => onFiltersChange({ ...filters, createdBefore: e.target.value || undefined })}
                className="flex-1 border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
        <div className="flex items-center space-x-2 text-red-700 dark:text-red-300">
          <XMarkIcon className="w-5 h-5" />
          <span className="font-medium">Error loading documents</span>
        </div>
        <p className="text-red-600 dark:text-red-400 mt-2">{error}</p>
      </div>
    );
  }

  // Show loading skeleton for initial load
  if (isLoading) {
    return (
      <div className={`bg-[var(--brand-surface-card)] rounded-lg shadow overflow-hidden min-h-[600px] ${className}`}>
        <TableSkeleton />
      </div>
    );
  }

  return (
    <div className={`bg-[var(--brand-surface-card)] rounded-lg shadow overflow-hidden min-h-[600px] ${className}`}>
      {/* Header with search and filters */}
      <div className="border-b border-[var(--brand-border)] p-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-[var(--brand-text-primary)]">
            Documents {totalDocuments > 0 && `(${totalDocuments})`}
          </h2>
          
          <div className="flex items-center space-x-2">
            {/* Search */}
            <form onSubmit={handleSearch} className="relative">
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search documents..."
                className="w-64 pl-10 pr-4 py-2 border border-[var(--brand-border)] rounded-lg bg-[var(--brand-surface-panel)] text-[var(--brand-text-primary)] placeholder:text-[var(--brand-text-tertiary)] focus:ring-2 focus:ring-[var(--brand-focus-ring)] focus:border-transparent"
              />
              <MagnifyingGlassIcon className="w-5 h-5 text-gray-400 absolute left-3 top-2.5" />
              {searchQuery && (
                <button
                  type="button"
                  onClick={handleSearchClear}
                  className="absolute right-3 top-2.5 text-[var(--brand-text-tertiary)] hover:text-[var(--brand-text-secondary)]"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              )}
            </form>
            
            {/* Filters toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`p-2 rounded-lg ${
                showFilters 
                  ? 'bg-[var(--brand-state-selected)] text-[var(--brand-accent-deep)]'
                  : 'bg-[var(--brand-surface-elevated)] text-[var(--brand-text-secondary)] hover:bg-[var(--brand-surface-hover)] hover:text-[var(--brand-text-primary)]'
              }`}
            >
              <FunnelIcon className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Filter panel */}
      <FilterPanel />

      {/* Conditional Rendering: Table or Card View */}
      {viewMode === 'grid' ? (
        /* Card Grid View */
        <div className="min-h-[400px]">
          {/* Grid Header with Select All */}
          <div className="flex items-center justify-between mb-4 px-2">
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                ref={selectAllCheckboxRef}
                checked={isAllSelected}
                onChange={handleSelectAll}
                className="w-4 h-4 text-lex-accent rounded border-gray-300 focus:ring-lex-accent"
              />
              <span className="text-sm text-[var(--brand-text-secondary)]">
                Select All ({documents.length} documents)
              </span>
            </div>
          </div>

          {/* Cards Grid */}
          {documents.length === 0 ? (
            <div className="text-center py-12 text-[var(--brand-text-secondary)]">
              No documents found
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {documents.map((document) => (
                <DocumentCard key={document.document_id} document={document} />
              ))}
            </div>
          )}
        </div>
      ) : (
        /* Table View */
      <div className="overflow-x-auto min-h-[400px]">
        <table className="w-full table-fixed">
          <colgroup>
            <col className="w-12" />
            <col />
            <col className="w-52" />
            <col className="w-44" />
            <col className="w-32" />
          </colgroup>
          <thead className="bg-[var(--brand-surface-elevated)]">
            <tr>
              <th className="w-12 px-4 py-3 text-left">
                <input
                  type="checkbox"
                  ref={selectAllCheckboxRef}
                  checked={isAllSelected}
                  onChange={handleSelectAll}
                  className="w-4 h-4 text-lex-accent rounded border-gray-300 focus:ring-lex-accent"
                />
              </th>
              
              <th className="px-4 py-3 text-left text-xs font-medium text-[var(--brand-text-secondary)] uppercase tracking-wider">
                <button
                  onClick={() => handleSort('title')}
                  className="flex items-center space-x-1 hover:text-[var(--brand-text-primary)]"
                >
                  <span>Document</span>
                  {getSortIcon('title')}
                </button>
              </th>
              
              <th className="px-4 py-3 text-left text-xs font-medium text-[var(--brand-text-secondary)] uppercase tracking-wider">
                Document Type
              </th>
              
              <th className="px-4 py-3 text-left text-xs font-medium text-[var(--brand-text-secondary)] uppercase tracking-wider">
                <button
                  onClick={() => handleSort('updated_at')}
                  className="flex items-center space-x-1 hover:text-[var(--brand-text-primary)]"
                >
                  <span>Modified</span>
                  {getSortIcon('updated_at')}
                </button>
              </th>
              
              <th className="px-4 py-3 text-right text-xs font-medium text-[var(--brand-text-secondary)] uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          
          <tbody className="bg-[var(--brand-surface-card)] divide-y divide-[var(--brand-border)]">
            {documents.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-6 py-8 text-center text-[var(--brand-text-secondary)]">
                  No documents found
                </td>
              </tr>
            ) : (
              documents.map((document) => (
                <DocumentRow key={document.document_id} document={document} />
              ))
            )}
          </tbody>
        </table>
      </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && <PaginationControls />}


    </div>
  );
};

export default React.memo(DocumentTable); 
